import ModeDataComposer from './ModeDataComposer';
import * as _ from 'lodash';

class ModeDataManager {

    constructor(dataFetcher) {
        this._dataFetcher = dataFetcher;
        this._preparePromise = null;
        this._composedData = null;
        this._metadata = null;

        this._ECOM_BOTH = "all";
        this._ECOM_ONLY = "ecom_only";
        this._ECOM_NONE = "ecom_none"
    }

    // Retrieve and compose data. The caller should wait on the returned promise to complete to indicate
    // the class is ready for use
    prepareData() {
        if (this._preparePromise === null) {
            this._preparePromise = this._dataFetcher.getData().then(rawData => {
                this._metadata = {};
                this._metadata.updated_times = rawData.meta;
                this._metadata.last_updated_min = _.chain(rawData.meta).values().min().value();
                this._metadata.last_updated_max = _.chain(rawData.meta).values().max().value();

                const composer = new ModeDataComposer(rawData);
                const cd = composer.getData();

                this._composedData = _.chain(cd);

                // nothing returned.
            });
        }

        return this._preparePromise;
    }

    getMetadata() {
        this._throwIfIncomplete();
        return this._metadata;
    }

    getData(filters, sort, search) {
        this._throwIfIncomplete();

        console.time("ModeDataManager.getData");
        const f = filters ? filters : {};
        const so = sort ? sort : {field: "arr_total", asc: false};
        const se = search ? search : null;

        const filtered = this._filter(this._composedData, f);
        const searched = this._search(filtered, se);
        const sorted = this._sort(searched, so);

        console.timeEnd("ModeDataManager.getData");

        return sorted.value();
    }

    getFilterAndSortOptions() {
        this._throwIfIncomplete();
        const fas = {filter_options:{}, sort_options: {}};
        fas.filter_options.csm = this._getCsmOptions();
        fas.filter_options.am = this._getAmOptions();
        fas.filter_options.ecom = this._getEcomOptions();
        fas.filter_options.booleans = [{name: "Red Acct", value: "is_red"}];
        fas.sort_options = this._getSortOptions();

        return fas;
    }

    _filter(data, filters) {
        return _.reduce(filters, (data, value, key) => {
            switch (key) {
                case "csm" : return data.filter(c => c.account.csm === value || value === "all" || (value === "null" && c.account.csm === null));
                case "am" : return data.filter(c => c.account.am === value || value === "all" || (value === "null" && c.account.am === null));
                case "ecom" : return data.filter(c => (c.account.has_ecom && value === this._ECOM_ONLY) || (!c.account.has_ecom && value === this._ECOM_NONE) || value === "all");
                case "is_red" : return data.filter(c => c.account.is_red_account === value);
                default: throw new Error("Unknown filter: " + key);
            }
        }, data);
    }

    _search(data, search) {
        let d = data;
        if (search && search.trim() !== "") {
            const s = search.toLowerCase();
            d = data.filter(item => {
                return item.account.client_name.toLowerCase().indexOf(s) > -1 ||
                    (item.account.parent_name && item.account.parent_name.toLowerCase().indexOf(s) > -1)
            });
        }

        return d;
    }

    _sort(data, sort) {

        // lodash sort is stable. Always sort by the name first and then the desired field, which results in
        // a sort of: sort.field sort.asc, client_name asc
        const nameSort = {field: "client_name", asc: sort.asc};
        const sorts = [nameSort, sort];

        return _.reduce(sorts, (dataSoFar, nextSort) => {
            const dir = nextSort.asc ? d => d : d => d.reverse();
            switch(nextSort.field) {
                case "app_sessions_mom":
                    return dir(dataSoFar.sortBy(item => item.app_sessions.weekly.four_week_delta));
                case "app_sessions_mom_p":
                    return dir(dataSoFar.sortBy(item => item.app_sessions.weekly.four_week_percentage_delta));
                case "app_sessions_wow":
                    return dir(dataSoFar.sortBy(item => item.app_sessions.weekly.delta));
                case "app_sessions_wow_p":
                    return dir(dataSoFar.sortBy(item => item.app_sessions.weekly.percentage_delta));
                case "arr_prorated":
                    return dir(dataSoFar.sortBy(item => item.account.arr_prorated));
                case "arr_total":
                    return dir(dataSoFar.sortBy(item => item.account.arr_total));
                case "client_name":
                    return dir(dataSoFar.sortBy(item => item.account.client_name));
                case "csm":
                    return dir(dataSoFar.sortBy(item => item.account.csm));
                case "health_score":
                    return dir(dataSoFar.sortBy(item => item.health_score.score));
                case "renewal_date":
                    return dir(dataSoFar.sortBy(item => Date.parse(item.account.renewal_date)));
                case "start_date":
                    return dir(dataSoFar.sortBy(item => Date.parse(item.account.start_date)));
                case "tagged_items_mom":
                    return dir(dataSoFar.sortBy(item => item.tagged_items.weekly.four_week_delta));
                case "tagged_items_mom_p":
                    return dir(dataSoFar.sortBy(item => item.tagged_items.weekly.four_week_percentage_delta));
                case "tagged_items_wow":
                    return dir(dataSoFar.sortBy(item => item.tagged_items.weekly.delta));
                case "tagged_items_wow_p":
                    return dir(dataSoFar.sortBy(item => item.tagged_items.weekly.percentage_delta));
                case "visits_mom":
                    return dir(dataSoFar.sortBy(item => item.visits.all.weekly.four_week_delta));
                case "visits_mom_p":
                    return dir(dataSoFar.sortBy(item => item.visits.all.weekly.four_week_percentage_delta));
                case "visits_wow":
                    return dir(dataSoFar.sortBy(item => item.visits.all.weekly.delta));
                case "visits_wow_p":
                    return dir(dataSoFar.sortBy(item => item.visits.all.weekly.percentage_delta));
                default :
                    throw new Error("Unknown sort field: " + sort.field);
            }
        }, data);
    }

    _getCsmOptions() {
        const csms = this._composedData.map(x => x.account.csm).uniq().sort();
        const nvp = csms.map(c =>  {
            const name = c ? c : "Null";
            const value = c ? c : "null";
            return {name: name, value: value}
        }).value();

        return _.concat([{name: "All", value: "all"}], nvp);
    }

    _getAmOptions() {
        const ams = this._composedData.map(x => x.account.am).uniq().sort();
        const nvp = ams.map(c =>  {
            const name = c ? c : "Null";
            const value = c ? c : "null";
            return {name: name, value: value}
        }).value();

        return _.concat([{name: "All", value: "all"}], nvp);
    }

    _getEcomOptions() {
        return [
            {name: "All", value: this._ECOM_BOTH},
            {name: "ECom Only", value: this._ECOM_ONLY},
            {name: "Non-ECom Only", value: this._ECOM_NONE}
        ];
    }

    _getSortOptions() {
        const sorts = [];

        const add = (n, v) => sorts.push({name: n, value: v });
        add("App Sessions MoM", "app_sessions_mom");
        add("App Sessions MoM %", "app_sessions_mom_p");
        add("App Sessions WoW", "app_sessions_wow");
        add("App Sessions WoW %", "app_sessions_wow_p");
        add("ARR (Total)", "arr_total");
        add("ARR (Prorated)", "arr_prorated");
        add("Client Name", "client_name");
        add("CSM", "csm");
        add("Health Score", "health_score");
        add("Renewal Date", "renewal_date");
        add("Start Date", "start_date");
        add("Tagged Items MoM", "tagged_items_mom");
        add("Tagged Items MoM %", "tagged_items_mom_p");
        add("Tagged Items WoW", "tagged_items_wow");
        add("Tagged Items WoW %", "tagged_items_wow_p")
        add("Visits MoM", "visits_mom");
        add("Visits MoM %", "visits_mom_p");
        add("Visits WoW", "visits_wow");
        add("Visits WoW %", "visits_wow_p")
        return sorts;
    }

    _throwIfIncomplete() {
        if (this._composedData === null) {
            throw Error("No data yet! Call prepareData and wait for the promise to complete.");
        }
    }
}

export default ModeDataManager;


// WEBPACK FOOTER //
// ./src/ModeDataManager.js